import React, { useState } from 'react';
import { Card, Form, Input, Button, message, Typography, Divider, Space } from 'antd';
import { LockOutlined, UserOutlined, MailOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import ProfileLinkResultsCard from '../components/ProfileLinkResultsCard';

const { Title, Text } = Typography;

const Profile: React.FC = () => {
  const { user, changePassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const handlePasswordChange = async (values: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('New password and confirmation do not match');
      return;
    }

    setLoading(true);
    try {
      const result = await changePassword(values.currentPassword, values.newPassword);
      
      if (result.success) {
        message.success('Password changed successfully!');
        form.resetFields();
      } else {
        message.error(result.error || 'Failed to change password');
      }
    } catch (error) {
      message.error('An unexpected error occurred');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <Title level={2}>Profile Settings</Title>
      
      <Card style={{ marginBottom: '24px' }}>
        <Title level={4}>Account Information</Title>
        <Divider />
        
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text strong><MailOutlined /> Email:</Text>
            <br />
            <Text>{user?.email || 'Not available'}</Text>
          </div>
          
          {user?.displayName && (
            <div style={{ marginTop: '16px' }}>
              <Text strong><UserOutlined /> Display Name:</Text>
              <br />
              <Text>{user.displayName}</Text>
            </div>
          )}
          
          <div style={{ marginTop: '16px' }}>
            <Text strong>User ID:</Text>
            <br />
            <Text code>{user?.uid}</Text>
          </div>
        </Space>
      </Card>

      <ProfileLinkResultsCard />

      <Card>
        <Title level={4}><LockOutlined /> Change Password</Title>
        <Divider />
        
        <Form
          form={form}
          layout="vertical"
          onFinish={handlePasswordChange}
          autoComplete="off"
        >
          <Form.Item
            name="currentPassword"
            label="Current Password"
            rules={[
              { required: true, message: 'Please enter your current password' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter current password"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter a new password' },
              { min: 6, message: 'Password must be at least 6 characters' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter new password (min 6 characters)"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Confirm new password"
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              block
            >
              Change Password
            </Button>
          </Form.Item>
        </Form>

        <Divider />
        
        <div style={{ padding: '12px', backgroundColor: 'var(--pale-sky)', borderRadius: '4px' }}>
          <Text type="secondary">
            <strong>Password Requirements:</strong>
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li>At least 6 characters long</li>
              <li>Should include a mix of letters, numbers, and symbols</li>
              <li>Avoid using common words or personal information</li>
            </ul>
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default Profile;

